import { Link } from "react-router-dom";
import { Phone, MapPin, Mail } from "lucide-react";
import solarLogo from "@/assets/solar-logo.png?format=webp&w=200";
import osioLogo from "@/assets/osio-logo.webp";
import { useLanguage, useLangPrefix } from "@/i18n/LanguageContext";

const Footer = () => {
  const lang = useLanguage();
  const prefix = useLangPrefix();

  const services = lang === "en"
    ? [
        { to: `${prefix}/blind-installation`, label: "Blind installation" },
        { to: `${prefix}/balcony-installation`, label: "Balcony blinds & roller blinds" },
        { to: `${prefix}/roller-blind-installation`, label: "Roller blind installation" },
        { to: `${prefix}/vertical-blind-installation`, label: "Vertical blind installation" },
        { to: `${prefix}/curtain-track-installation`, label: "Curtain track installation" },
      ]
    : [
        { to: "/kaihdin-asennus", label: "Kaihdin asennus" },
        { to: "/parveke-asennus", label: "Parvekkeiden kaihtimet ja rullaverhot" },
        { to: "/rullaverho-asennus", label: "Rullaverho asennus" },
        { to: "/lamelliverho-asennus", label: "Lamelliverho asennus" },
        { to: "/verhokisko-asennus", label: "Verhokisko asennus" },
      ];

  return (
    <footer className="bg-primary text-primary-foreground" role="contentinfo">
      <div className="container py-16">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <h3 className="font-display text-xl font-bold mb-4">Keski-Uudenmaan Kaihdin</h3>
            <p className="text-primary-foreground/70 text-sm leading-relaxed mb-4">
              Tmi Teppo Honkanen<br />
              {lang === "en" ? "Business ID" : "Y-tunnus"}: 2303843-1
            </p>
            <address className="not-italic space-y-2 text-sm text-primary-foreground/80">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 shrink-0" aria-hidden="true" />
                Iltalenkki 23, 05400 Jokela
              </div>
              <a href="tel:0405839991" className="flex items-center gap-2 hover:text-primary-foreground transition-colors">
                <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
                040 583 9991
              </a>
              <a href="mailto:info@ku-kaihdin.fi" className="flex items-center gap-2 hover:text-primary-foreground transition-colors">
                <Mail className="w-4 h-4 shrink-0" aria-hidden="true" />
                info@ku-kaihdin.fi
              </a>
            </address>
          </div>

          <div>
            <h4 className="font-display text-lg font-semibold mb-4">{lang === "en" ? "Services" : "Palvelut"}</h4>
            <nav className="space-y-2 text-sm text-primary-foreground/70" aria-label={lang === "en" ? "Services" : "Palvelut"}>
              {services.map((s) => (
                <Link key={s.to} to={s.to} className="block hover:text-primary-foreground transition-colors">{s.label}</Link>
              ))}
            </nav>
          </div>

          <div>
            <h4 className="font-display text-lg font-semibold mb-4">{lang === "en" ? "Partners" : "Yhteistyökumppanit"}</h4>
            <div className="flex items-center gap-3">
              <a href="https://solar.fi" target="_blank" rel="noopener noreferrer" className="inline-block bg-primary-foreground rounded-md p-3 hover:opacity-90 transition-opacity">
                <img src={solarLogo} alt="Solar Kaihdin logo" className="h-8" />
              </a>
              <a href="https://osio.fi" target="_blank" rel="noopener noreferrer" className="inline-block bg-primary-foreground rounded-md p-3 hover:opacity-90 transition-opacity">
                <img src={osioLogo} alt="Osio logo" className="h-8" />
              </a>
            </div>
            <p className="text-primary-foreground/70 text-sm mt-3">
              {lang === "en"
                ? "We install high-quality products from Solar Kaihdin and Osio."
                : "Asennamme Solar Kaihtimen ja Osion laadukkaita tuotteita."}
            </p>
          </div>
        </div>

        <div className="border-t border-primary-foreground/20 mt-12 pt-6 text-center text-xs text-primary-foreground/60 space-y-1">
          <div>
            <Link to={lang === "en" ? "/en/privacy-policy" : "/tietosuoja"} className="hover:text-primary-foreground transition-colors underline">
              {lang === "en" ? "Privacy Policy" : "Tietosuojaseloste"}
            </Link>
          </div>
          <div>© {new Date().getFullYear()} Keski-Uudenmaan Kaihdin. {lang === "en" ? "All rights reserved." : "Kaikki oikeudet pidätetään."}</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
